import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Bold, Italic, Underline, AlignLeft, AlignCenter, AlignRight, AlignJustify } from 'lucide-react';
import type { Font } from '@customedge/shared';
import { ColorPicker } from './ColorPicker';
import { Slider } from '../Slider';
import { ToolSection } from './shared/ToolSection';
import { PropertyRow, propertyInputStyle, PANEL_FIELD_CLASS } from './shared/PropertyRow';
import { segmentedGroup, segmentBtn, iconSegmentBtn } from './shared/SegmentedControl';
import { FontCombobox } from './shared/FontCombobox';
import { getLogicalSize, centerObjectLogical } from '../../lib/canvasObjectHelpers';
import { ensureFontLoaded } from '../../lib/fontLoader';
import { useDesignStore } from '../../store/designStore';
import { useColorBudget, findOffendingObject, selectOffendingObject } from '../../hooks/useColorBudget';
import type { FabricCanvas } from '../../types/fabric';

interface TextToolProps {
  canvas: FabricCanvas | null;
}

async function fetchFonts() {
  const r = await fetch('/api/fonts');
  const d = await r.json();
  return (d.fonts ?? []) as Font[];
}

// Default text fills this fraction of the placeholder's width — matches the
// same 85% target used for images/clipart/shapes (see FIT_RATIO in
// canvasObjectHelpers.ts) so every tool drops new content at a consistent,
// already-readable size relative to the design area instead of a fixed
// pixel value that's tiny on a large placeholder and overflowing on a small one.
const TEXT_FIT_RATIO = 0.85;
// fontSize as a fraction of the *fitted* text width, tuned so the placeholder
// string ("Uw tekst hier" / 13 chars) roughly fills that width at a typical
// font's average glyph-width-to-fontSize ratio (~0.55) — i.e. fontSize ≈
// fittedWidth / (13 * 0.55). Clamped so tiny/huge placeholders still get a
// usable, legible default.
const dynamicFontSize = (fittedWidth: number) => Math.round(Math.min(96, Math.max(12, fittedWidth / 7)));

export function TextTool({ canvas }: TextToolProps) {
  const { t } = useTranslation();
  const activeSide = useDesignStore(state => state.sides.find(s => s.sideKey === state.activeSideKey));
  const colorBudget = useColorBudget(activeSide);
  // A pick blocked here has no "own object" to exclude yet (this text hasn't
  // been added to canvas), so the offending object is simply whichever real
  // object on the current canvas is holding up the limit — jump straight to
  // it per direct user request ("navigate them to correct the color in
  // existing object") instead of leaving the customer to hunt for it.
  const handleColorBlocked = () => {
    if (!activeSide || !canvas) return;
    const offending = findOffendingObject(canvas, activeSide);
    if (offending) selectOffendingObject(canvas, offending);
  };
  const { data: fonts = [] } = useQuery({ queryKey: ['fonts'], queryFn: fetchFonts, staleTime: 300_000 });
  const [textType, setTextType] = useState<'text' | 'curved' | 'vertical'>('text');
  const [textValue, setTextValue] = useState(() => t('text.placeholder'));
  const [fontFamily, setFontFamily] = useState('Poppins');
  const [fontSize, setFontSize] = useState(20);
  // Tracks whether the user has manually edited fontSize/diameter this side visit —
  // once they have, the placeholder-width auto-sizing below must stop overriding
  // their choice (same "never override an actual user selection" rule the
  // existing fontFamily-sync effect already follows).
  const [sizeTouched, setSizeTouched] = useState(false);
  const [color, setColor] = useState('#000000');
  const [diameter, setDiameter] = useState(250);
  const [bold, setBold] = useState(false);
  const [italic, setItalic] = useState(false);
  const [underline, setUnderline] = useState(false);
  const [charSpacing, setCharSpacing] = useState(0);
  // Defaults to 'center', not 'left': the Textbox itself is centered on the
  // placeholder via canvas.centerObject(), but that only centers the (wide,
  // 85%-of-placeholder) bounding box — left-aligned glyphs inside a box much
  // wider than the text visually read as "stuck in the top-left corner" of
  // the placeholder even though the invisible box was correctly centered.
  const [textAlign, setTextAlign] = useState<'left' | 'center' | 'right' | 'justify'>('center');
  const [opacity, setOpacity] = useState(1);
  const [strokeColor, setStrokeColor] = useState('#000000');
  const [strokeWidth, setStrokeWidth] = useState(0);
  const [angle, setAngle] = useState(0);

  // `fontFamily` defaults to 'Poppins', which isn't guaranteed to actually
  // exist in this install's font library (this dev DB has none named that).
  // The <select> below is properly controlled (value={fontFamily}), but a
  // browser <select> whose bound value matches no <option> still visually
  // shows the first option as selected — so the dropdown can *look* like a
  // real font is chosen while `fontFamily` state (and therefore every object
  // addText() constructs) is still silently using the never-loaded default.
  // Sync to the first real font as soon as the list loads, but only while
  // the current value doesn't match a real entry — never overrides an
  // actual user selection.
  useEffect(() => {
    if (fonts.length === 0) return;
    if (fonts.some(f => f.fontLabel === fontFamily)) return;
    const first = fonts[0];
    setFontFamily(first.fontLabel);
    if (first.fontFile) ensureFontLoaded(first.fontLabel, `/storage/${first.fontFile}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fonts]);

  // Re-derive the default font size (and curved diameter) from the active
  // placeholder's real width every time the user switches to a side with a
  // different design area — a fixed `fontSize: 20` default is invisible on a
  // 1200px-wide placeholder and overflowing on a 150px one. Only applies
  // while the user hasn't manually typed a size this visit; switching sides
  // resets that guard so the new side gets its own correctly-scaled default
  // rather than carrying over a value tuned for the previous placeholder.
  useEffect(() => {
    setSizeTouched(false);
  }, [canvas]);

  useEffect(() => {
    if (sizeTouched || !canvas) return;
    const { width: cw, height: ch } = getLogicalSize(canvas);
    const fittedWidth = cw * TEXT_FIT_RATIO;
    setFontSize(dynamicFontSize(fittedWidth));
    setDiameter(Math.round(Math.min(cw, ch) * TEXT_FIT_RATIO));
  }, [canvas, sizeTouched]);
  const isTextEmpty = !textValue.trim();
  const addText = async () => {
    if (!canvas) return;
    if (!textValue.trim()) return;
    if (isTextEmpty) return;
    try {
      const { fabric } = await import('fabric');

      // Load the selected font in the browser before adding to canvas
      const fontRecord = fonts.find(f => f.fontLabel === fontFamily);
      if (fontRecord?.fontFile) {
        await ensureFontLoaded(fontFamily, `/storage/${fontRecord.fontFile}`);
      }

      const { width: cw, height: ch } = getLogicalSize(canvas);

      if (textType === 'curved') {
        if (!(fabric as any).CurvedText) {
          console.error('CurvedText not registered — ensure useFabricCanvas has initialised first');
          return;
        }
        // Cap the diameter so a freshly-added curved text fits inside small placeholders by default
        const fittedDiameter = Math.min(diameter, Math.min(cw, ch) * TEXT_FIT_RATIO);
        // fabric.curvedText.ts's render math divides by (diameter/2 - textHeight),
        // where textHeight scales with fontSize (~1.2-1.4x in most fonts) — capping
        // only the diameter without also capping fontSize lets that denominator
        // collapse toward zero/negative on small canvases, rendering the curve
        // invisible or garbled instead of just small. Keep comfortable curvature
        // room (radius at least ~2.8x the worst-case text height) by shrinking the
        // font size to match, the same way the diameter itself is already shrunk.
        const radius = fittedDiameter / 2;
        const fittedFontSize = Math.max(1, Math.min(fontSize, Math.floor(radius / 2.8)));
        const obj = new (fabric as any).CurvedText(textValue, {
          diameter: fittedDiameter, fontFamily, fontSize: fittedFontSize, fill: color,
          fontWeight: bold ? 'bold' : 'normal', fontStyle: italic ? 'italic' : '',
          strokeStyle: strokeWidth > 0 ? strokeColor : null, strokeWidth,
          opacity, angle,
        });
        canvas.add(obj); centerObjectLogical(canvas, obj); canvas.setActiveObject(obj); canvas.renderAll();
      } else if (textType === 'vertical') {
        // Stack characters one per line so they read top-to-bottom instead of
        // left-to-right — plain Textbox/Text have no native vertical writing
        // mode in Fabric, so this is just the text content itself rearranged
        // (kept in sync below whenever the source textValue changes).
        const stacked = textValue.split('').join('\n');
        const vWidth = Math.min(fontSize * 1.6, cw * TEXT_FIT_RATIO);
        const obj = new fabric.Textbox(stacked, {
          width: vWidth, fontSize, fontFamily, fill: color,
          textAlign: 'center', lineHeight: 1,
          fontWeight: bold ? 'bold' : 'normal', fontStyle: italic ? 'italic' : 'normal',
          underline, charSpacing, opacity, angle,
          stroke: strokeWidth > 0 ? strokeColor : undefined, strokeWidth,
        });
        (obj as any).isVerticalText = true;
        canvas.add(obj); centerObjectLogical(canvas, obj); canvas.setActiveObject(obj); canvas.renderAll();
      } else {
        const tbWidth = cw * TEXT_FIT_RATIO;
        const obj = new fabric.Textbox(textValue, {
          width: tbWidth, fontSize, fontFamily, fill: color,
          fontWeight: bold ? 'bold' : 'normal', fontStyle: italic ? 'italic' : 'normal',
          underline, charSpacing, textAlign, opacity, angle,
          stroke: strokeWidth > 0 ? strokeColor : undefined, strokeWidth,
        });
        canvas.add(obj); centerObjectLogical(canvas, obj); canvas.setActiveObject(obj); canvas.renderAll();
      }
    } catch (err) {
      console.error('Failed to add text to canvas:', err);
    }
  };

  // Also load font on selection change so the preview textarea renders it immediately
  const handleFontChange = async (label: string) => {
    setFontFamily(label);
    const fontRecord = fonts.find(f => f.fontLabel === label);
    if (fontRecord?.fontFile) {
      await ensureFontLoaded(label, `/storage/${fontRecord.fontFile}`);
    }
  };

  return (
    <div style={{ padding: '12px 12px 0', display: 'flex', flexDirection: 'column' }}>
      <ToolSection title={t('text.sectionContent')} divider={false}>
        <div style={segmentedGroup}>
          {(['text', 'curved', 'vertical'] as const).map(type => (
            <button key={type} onClick={() => setTextType(type)} style={segmentBtn(textType === type)}>
              {type === 'text' ? t('text.straight') : type === 'curved' ? t('text.curved') : t('text.vertical')}
            </button>
          ))}
        </div>

        <textarea value={textValue} onChange={e => setTextValue(e.target.value)}
          className={PANEL_FIELD_CLASS}
          style={{ ...propertyInputStyle, resize: 'vertical', minHeight: 48, fontFamily, fontSize: 14 }} />
      </ToolSection>

      <ToolSection title={t('text.sectionTypography')}>
        <FontCombobox fonts={fonts} value={fontFamily} onChange={handleFontChange} />

        <PropertyRow label={t('text.size')}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flex: 1 }}>
            <Slider min={6} max={200} step={1} value={fontSize}
              onChange={v => { setFontSize(v); setSizeTouched(true); }} />
            <input type="number" value={fontSize}
              onChange={e => { setFontSize(Number(e.target.value)); setSizeTouched(true); }}
              min={6} max={200} className={PANEL_FIELD_CLASS} style={{ ...propertyInputStyle, width: 48 }} />
          </div>
        </PropertyRow>

        {textType === 'curved' && (
          <PropertyRow label={t('text.diameter')}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flex: 1 }}>
              <Slider min={50} max={1000} step={10} value={diameter}
                onChange={v => { setDiameter(v); setSizeTouched(true); }} />
              <input type="number" value={diameter}
                onChange={e => { setDiameter(Number(e.target.value)); setSizeTouched(true); }}
                min={50} max={1000} className={PANEL_FIELD_CLASS} style={{ ...propertyInputStyle, width: 48 }} />
            </div>
          </PropertyRow>
        )}
      </ToolSection>

      <ToolSection title={t('text.sectionStyle')}>
        <ColorPicker value={color} onChange={setColor}
          isColorAllowed={colorBudget.isColorAllowed}
          usedColors={colorBudget.usedColors}
          isAtLimit={colorBudget.isAtLimit}
          onBlocked={handleColorBlocked}
          budgetLabel={colorBudget.limit != null ? `${colorBudget.usedColors.length} van ${colorBudget.limit} kleuren gebruikt` : undefined} />

        <div style={segmentedGroup}>
          <button onClick={() => setBold(b => !b)} title={t('properties.bold')} style={{ ...iconSegmentBtn(bold), flex: 1 }}>
            <Bold size={14} strokeWidth={1.75} />
          </button>
          <button onClick={() => setItalic(i => !i)} title={t('properties.italic')} style={{ ...iconSegmentBtn(italic), flex: 1 }}>
            <Italic size={14} strokeWidth={1.75} />
          </button>
          <button onClick={() => setUnderline(u => !u)} title={t('properties.underline')} style={{ ...iconSegmentBtn(underline), flex: 1 }}>
            <Underline size={14} strokeWidth={1.75} />
          </button>
        </div>

        <PropertyRow label={t('properties.letterSpacing')}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flex: 1 }}>
            <Slider min={-200} max={1000} step={10} value={charSpacing} onChange={setCharSpacing} />
            <input type="number" value={charSpacing} onChange={e => setCharSpacing(Number(e.target.value))}
              min={-200} max={1000} step={10} className={PANEL_FIELD_CLASS} style={{ ...propertyInputStyle, width: 48 }} />
          </div>
        </PropertyRow>

        {textType !== 'curved' && (
          <div style={segmentedGroup}>
            {([
              ['left', AlignLeft], ['center', AlignCenter],
              ['right', AlignRight], ['justify', AlignJustify],
            ] as const).map(([a, Icon]) => (
              <button
                key={a}
                onClick={() => setTextAlign(a)}
                title={t(`properties.align${a.charAt(0).toUpperCase()}${a.slice(1)}`)}
                style={{ ...iconSegmentBtn(textAlign === a), flex: 1 }}
              >
                <Icon size={14} strokeWidth={1.75} />
              </button>
            ))}
          </div>
        )}
      </ToolSection>

      <ToolSection title={t('text.sectionEffects')}>
        <PropertyRow label={t('properties.opacity')}>
          <Slider min={0} max={1} step={0.05} value={opacity} onChange={setOpacity}
            valueLabel={`${Math.round(opacity * 100)}%`} />
        </PropertyRow>

        <ColorPicker value={strokeColor} onChange={setStrokeColor}
          label={t('properties.strokeColor')}
          isColorAllowed={colorBudget.isColorAllowed}
          usedColors={colorBudget.usedColors}
          isAtLimit={colorBudget.isAtLimit}
          onBlocked={handleColorBlocked} />
        <PropertyRow label={t('properties.strokeWidth')}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flex: 1 }}>
            <Slider min={0} max={50} step={1} value={strokeWidth} onChange={setStrokeWidth} />
            <input type="number" value={strokeWidth} onChange={e => setStrokeWidth(Number(e.target.value))}
              min={0} max={50} className={PANEL_FIELD_CLASS} style={{ ...propertyInputStyle, width: 48 }} />
          </div>
        </PropertyRow>
      </ToolSection>

      {/* Transform — rotation only, applied as the new object's initial angle
          when "Add text" is clicked. Placed last (matches PropertyPanel.tsx's
          Edit panel, which moved its own Transform section to the bottom for
          the same reason): it's the least-used control for a NEW piece of
          text, so it shouldn't sit above the fields customers touch on every
          add (content, font, color). No flip here — flipping text before it
          even exists on canvas has nothing to preview against, so (per
          direct user request) Transform in Add is rotation-only. */}
      <ToolSection title={t('properties.transform')}>
        <PropertyRow label={t('properties.rotation')}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flex: 1 }}>
            <Slider min={-180} max={180} step={1} value={angle} onChange={setAngle} />
            <input type="number" value={angle} min={-180} max={180}
              onChange={e => setAngle(Number(e.target.value))}
              className={PANEL_FIELD_CLASS} style={{ ...propertyInputStyle, width: 48 }} />
            <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>°</span>
          </div>
        </PropertyRow>
      </ToolSection>

      {/* <button onClick={addText} className="btn btn-primary" style={{ width: '100%', marginTop: 12 }}>
        {t('text.addText')}
      </button> */}
       <div
        style={{
          position: 'sticky',
          bottom: 0,
          zIndex: 2,
          marginTop: 12,
          padding: '12px 0',
          background: 'var(--bg-panel, #fff)',
          borderTop: '1px solid var(--border, #eee)',
        }}
      >
        <button onClick={addText} disabled={isTextEmpty} className="btn btn-primary" style={{ width: '100%',opacity: isTextEmpty ? 0.5 : 1,cursor: isTextEmpty ? 'not-allowed' : 'pointer', }}>
          {t('text.addText')}
        </button>
      </div>
    </div>
  );
}
